import type { SubcategorySettings } from "../../domain/app-types.ts";
import type { TaskSort } from "../../domain/entities.ts";
import { normalizeSortDirection } from "../tasks/selectors.ts";
import { normalizeBoardWipLimits } from "../tasks/board-wip.ts";
import { normalizeBoardAutomation, normalizeBoardCardFields, normalizeBoardColumnSettings } from "../tasks/board-configuration.ts";
import type { ApprovalRule, BoardAutomationSettings, BoardColumnSettings } from "../../domain/entities.ts";

export interface CollectionSettingsInput {
  name: string;
  viewType: "list" | "board" | "timeline" | "table";
  boardColumns: string;
  boardWipLimits?: Record<string, number | undefined>;
  boardColumnSettings?: Record<string, Partial<BoardColumnSettings> | undefined>;
  boardCardFields?: string[];
  boardAutomation?: Partial<BoardAutomationSettings>;
  approvalRule?: ApprovalRule | null;
  sortBy: TaskSort;
  sortDirection: string;
  pageSize: number;
  showCompleted: boolean;
  defaultPriority?: SubcategorySettings["defaultPriority"];
  defaultTags?: string;
  defaultAssigneeIds?: string[];
  defaultTaskType?: SubcategorySettings["defaultTaskType"];
  defaultTemplateId?: string;
  defaultDueOffsetDays?: number;
}

export function collectionSettings(input: CollectionSettingsInput): SubcategorySettings | null {
  const name = input.name.trim();
  if (!name) return null;
  const boardColumns = [...new Set(input.boardColumns.split(",").map(column => column.trim().toLowerCase()).filter(Boolean))];
  if (input.viewType === "board" && !boardColumns.length) return null;
  return {
    name,
    viewType: input.viewType,
    boardColumns,
    boardWipLimits: normalizeBoardWipLimits(boardColumns, input.boardWipLimits),
    boardColumnSettings: normalizeBoardColumnSettings(boardColumns, input.boardColumnSettings),
    boardCardFields: normalizeBoardCardFields(input.boardCardFields),
    boardAutomation: normalizeBoardAutomation(boardColumns, input.boardAutomation),
    approvalRule: input.approvalRule?.reviewerIds.length ? { column: input.viewType === "board" && input.approvalRule.column && boardColumns.includes(input.approvalRule.column) ? input.approvalRule.column : null, reviewerIds: [...new Set(input.approvalRule.reviewerIds)] } : null,
    sortBy: input.sortBy,
    sortDirection: normalizeSortDirection(input.sortDirection),
    pageSize: input.pageSize,
    showCompleted: input.showCompleted,
    defaultPriority: input.defaultPriority || "none",
    defaultTags: [...new Set((input.defaultTags || "").split(",").map(tag => tag.trim().toLowerCase()).filter(Boolean))],
    defaultAssigneeIds: input.defaultAssigneeIds || [],
    defaultTaskType: input.defaultTaskType || "text",
    defaultTemplateId: input.defaultTemplateId || null,
    defaultDueOffsetDays: Number.isFinite(input.defaultDueOffsetDays) ? input.defaultDueOffsetDays! : null,
  };
}
